import { useEffect, useState, useRef } from 'react'
import { Upload, AlertCircle, CheckCircle, XCircle, Clock, FileText, X } from 'lucide-react'
import { Text } from '@/components/tailwind/text'
import { Button } from '@/components/tailwind/button'
import { Badge } from '@/components/tailwind/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/tailwind/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch, SwitchField } from '@/components/tailwind/switch'
import { Field, Label } from '@/components/tailwind/fieldset'
import { Input } from '@/components/tailwind/input'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/tailwind/dialog'
import { useAdminTranslation } from "@/i18n/hooks"
import { useAuth } from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'
import * as importService from '@/services/import-services'
import type { ImportStatusResponse, ImportSessionSummary, ImportStatus, ImportPhase } from '@/services/import-services'
import {getTodayLocalDateString} from "@/helpers/date-helpers.ts";
import log from '@/lib/logger'

interface SectionImportSettingsProps {
    isMissing?: boolean
}

const DISMISSED_WARNING_KEY = 'nbh.import.dismissedSocialWarning'

export function SectionImportSettings({ isMissing = false }: SectionImportSettingsProps) {
    const { t } = useAdminTranslation()
    const { token } = useAuth()
    const { toast } = useToast()
    const navigate = useNavigate()

    // State
    const [loading, setLoading] = useState(true)
    const [uploading, setUploading] = useState(false)
    const [importing, setImporting] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [currentStatus, setCurrentStatus] = useState<ImportStatusResponse | null>(null)
    const [history, setHistory] = useState<ImportSessionSummary[]>([])
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [openingBalanceEnabled, setOpeningBalanceEnabled] = useState(false)
    const [openingBalanceAmount, setOpeningBalanceAmount] = useState<string>('')
    const [openingBalanceDate, setOpeningBalanceDate] = useState<string>(getTodayLocalDateString())
    const [isSocialConfirmOpen, setIsSocialConfirmOpen] = useState(false)
    const [socialConfirmAmount, setSocialConfirmAmount] = useState<string>('')
    const [confirmingSocial, setConfirmingSocial] = useState(false)
    const [dismissedWarningSessionId, setDismissedWarningSessionId] = useState<string | null>(
        () => localStorage.getItem(DISMISSED_WARNING_KEY)
    )

    const fileInputRef = useRef<HTMLInputElement>(null)
    const pollingIntervalRef = useRef<number | null>(null)

    // Load initial data
    useEffect(() => {
        loadData()
    }, [token])

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
            }
        }
    }, [])

    async function loadData() {
        if (!token) return

        try {
            setLoading(true)

            // Load current status and history in parallel
            const [status, sessions] = await Promise.all([
                importService.getImportStatus(token),
                importService.getImportHistory(token)
            ])

            setCurrentStatus(status)
            setHistory(sessions || [])

            // If there's a running import, start polling
            if (status && status.status === 'RUNNING') {
                setImporting(true)
                startPolling()
            } else {
                promptSocialConfirmationIfPending(status)
            }
        } catch (err: any) {
            log.error('Error loading import data:', err)
            toast({
                title: t('adminSettings.import.toast.loadError.title'),
                description: t('adminSettings.import.toast.loadError.description'),
                variant: 'destructive'
            })
        } finally {
            setLoading(false)
        }
    }

    function startPolling() {
        // Clear any existing interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
        }

        // Poll every 2 seconds
        pollingIntervalRef.current = window.setInterval(async () => {
            if (!token) return

            try {
                const status = await importService.getImportStatus(token)
                setCurrentStatus(status)

                // Stop polling if completed or failed
                if (status && (status.status === 'COMPLETED' || status.status === 'FAILED')) {
                    stopPolling()
                    setImporting(false)

                    if (status.status === 'COMPLETED') {
                        toast({
                            title: t('adminSettings.import.toast.importCompleted.title'),
                            description: t('adminSettings.import.toast.importCompleted.description')
                        })

                        // A staged Sozialkonto balance must be confirmed here; only redirect otherwise.
                        if (!promptSocialConfirmationIfPending(status)) {
                            setTimeout(() => {
                                navigate('/members')
                            }, 2000)
                        }
                    } else {
                        toast({
                            title: t('adminSettings.import.toast.importFailed.title'),
                            description: t('adminSettings.import.toast.importFailed.description', {
                                error: status.errors || 'Unknown error'
                            }),
                            variant: 'destructive'
                        })
                    }

                    // Reload history
                    loadHistory()
                }
            } catch (err) {
                log.error('Error polling import status:', err)
                // Don't show toast for every polling error - might be temporary
            }
        }, 2000)
    }

    function stopPolling() {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
        }
    }

    async function loadHistory() {
        if (!token) return

        try {
            const sessions = await importService.getImportHistory(token)
            setHistory(sessions || [])
        } catch (err) {
            log.error('Error loading import history:', err)
        }
    }

    // Re-surfaces a still-unconfirmed Sozialkonto balance so it is never lost on a disconnect.
    function promptSocialConfirmationIfPending(status: ImportStatusResponse | null): boolean {
        if (status && status.status === 'COMPLETED' && status.socialAccountConfirmationStatus === 'PENDING') {
            setSocialConfirmAmount(String(status.socialAccountDetectedNet ?? 0))
            setIsSocialConfirmOpen(true)
            return true
        }
        return false
    }

    async function handleConfirmSocialBalance() {
        if (!token || !currentStatus) return

        const amount = Number(socialConfirmAmount)
        if (!Number.isInteger(amount)) {
            toast({
                title: t('adminSettings.import.socialAccount.dialog.amountInvalid'),
                variant: 'destructive'
            })
            return
        }

        try {
            setConfirmingSocial(true)
            const result = await importService.confirmSocialAccountBalance(currentStatus.id, amount, token)
            toast({
                title: t('adminSettings.import.socialAccount.toast.confirmed.title'),
                description: t('adminSettings.import.socialAccount.toast.confirmed.description', {
                    balance: result.balance
                })
            })
            setIsSocialConfirmOpen(false)
            await loadData()
        } catch (err: any) {
            log.error('Error confirming social account balance:', err)
            toast({
                title: t('adminSettings.import.socialAccount.toast.confirmError.title'),
                description: t('adminSettings.import.socialAccount.toast.confirmError.description'),
                variant: 'destructive'
            })
        } finally {
            setConfirmingSocial(false)
        }
    }

    function handleFileSelect(file: File | null) {
        if (!file) {
            setSelectedFile(null)
            return
        }

        // Client-side validation
        if (!file.name.toLowerCase().endsWith('.mdb')) {
            toast({
                title: t('adminSettings.import.validation.invalidExtension'),
                description: t('adminSettings.import.errors.INVALID_EXTENSION'),
                variant: 'destructive'
            })
            return
        }

        const maxSize = 100 * 1024 * 1024 // 100 MB
        if (file.size > maxSize) {
            toast({
                title: t('adminSettings.import.validation.fileTooLarge'),
                description: t('adminSettings.import.errors.FILE_TOO_LARGE'),
                variant: 'destructive'
            })
            return
        }

        if (file.size === 0) {
            toast({
                title: t('adminSettings.import.validation.fileEmpty'),
                description: t('adminSettings.import.errors.FILE_EMPTY'),
                variant: 'destructive'
            })
            return
        }

        setSelectedFile(file)
        setIsDialogOpen(true)
    }

    function handleDialogClose() {
        setIsDialogOpen(false)
        setSelectedFile(null)
        setOpeningBalanceEnabled(false)
        setOpeningBalanceAmount('')
        setOpeningBalanceDate(getTodayLocalDateString())
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    function validateOpeningBalance(): boolean {
        if (!openingBalanceEnabled) return true

        if (!openingBalanceAmount || !openingBalanceDate) {
            toast({
                title: t('adminSettings.import.upload.openingBalance.validation.bothRequired'),
                variant: 'destructive'
            })
            return false
        }

        const amount = parseFloat(openingBalanceAmount)
        if (isNaN(amount)) {
            toast({
                title: t('adminSettings.import.upload.openingBalance.validation.amountRequired'),
                variant: 'destructive'
            })
            return false
        }

        if (amount < 0) {
            toast({
                title: t('adminSettings.import.upload.openingBalance.validation.amountNegative'),
                variant: 'destructive'
            })
            return false
        }

        return true
    }

    async function handleUpload() {
        if (!selectedFile || !token) return

        if (!validateOpeningBalance()) return

        try {
            setUploading(true)

            const amount = openingBalanceEnabled && openingBalanceAmount
                ? parseFloat(openingBalanceAmount)
                : undefined
            const date = openingBalanceEnabled && openingBalanceDate
                ? openingBalanceDate
                : undefined

            await importService.uploadMdbFile(selectedFile, token, amount, date)

            toast({
                title: t('adminSettings.import.toast.uploadSuccess.title'),
                description: t('adminSettings.import.toast.uploadSuccess.description')
            })

            // Upload successful, close dialog and start monitoring
            setIsDialogOpen(false)
            setImporting(true)
            setSelectedFile(null)
            setOpeningBalanceEnabled(false)
            setOpeningBalanceAmount('')
            setOpeningBalanceDate(getTodayLocalDateString())
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }

            // Start polling for status updates
            startPolling()

        } catch (err: any) {
            log.error('Error uploading file:', err)

            // Map backend errors to translated messages
            let errorMessage = t('adminSettings.import.toast.uploadError.description')

            if (err.message && err.message.includes('already running')) {
                errorMessage = t('adminSettings.import.errors.IMPORT_ALREADY_RUNNING')
                toast({
                    title: t('adminSettings.import.toast.concurrentImport.title'),
                    description: errorMessage,
                    variant: 'destructive'
                })
            } else {
                toast({
                    title: t('adminSettings.import.toast.uploadError.title'),
                    description: errorMessage,
                    variant: 'destructive'
                })
            }
        } finally {
            setUploading(false)
        }
    }

    function getStatusBadge(status: ImportStatus) {
        switch (status) {
            case 'COMPLETED':
                return <Badge color="green"><CheckCircle className="w-3 h-3 mr-1" />{t('adminSettings.import.progress.status.COMPLETED')}</Badge>
            case 'FAILED':
                return <Badge color="red"><XCircle className="w-3 h-3 mr-1" />{t('adminSettings.import.progress.status.FAILED')}</Badge>
            case 'RUNNING':
                return <Badge color="blue"><Clock className="w-3 h-3 mr-1" />{t('adminSettings.import.progress.status.RUNNING')}</Badge>
            default:
                return <Badge color="zinc">{status}</Badge>
        }
    }

    function formatPhase(phase: ImportPhase): string {
        return t(`adminSettings.import.progress.phases.${phase}`)
    }

    // Backend stores stable warning codes (newline-separated); localize each here.
    function formatWarnings(warnings: string): string {
        return warnings
            .split('\n')
            .map((code) => t(`adminSettings.import.socialAccount.warnings.${code}`, { defaultValue: code }))
            .join('\n')
    }

    function dismissWarning() {
        if (!currentStatus) return
        localStorage.setItem(DISMISSED_WARNING_KEY, currentStatus.id)
        setDismissedWarningSessionId(currentStatus.id)
    }

    function formatTimestamp(timestamp: string | null): string {
        if (!timestamp) return '-'

        return new Intl.DateTimeFormat('de-DE', {
            dateStyle: 'short',
            timeStyle: 'short'
        }).format(new Date(timestamp))
    }

    function formatDuration(durationSeconds: number | null): string {
        if (!durationSeconds) return '-'

        const minutes = Math.floor(durationSeconds / 60)
        const seconds = durationSeconds % 60

        if (minutes > 0) {
            return `${minutes} min ${seconds} s`
        }
        return `${seconds} s`
    }

    function formatDateRange(startDate: string | null, endDate: string | null): string {
        if (!startDate || !endDate) return '-'

        const format = new Intl.DateTimeFormat('de-DE', {
            year: 'numeric',
            month: 'short'
        })

        const start = format.format(new Date(startDate))
        const end = format.format(new Date(endDate))

        // If same month and year, show only once
        if (start === end) {
            return start
        }

        return `${start} - ${end}`
    }

    if (loading) {
        return (
            <Card>
                <CardContent className="p-8">
                    <div className="flex justify-center">
                        <LoadingSpinner />
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <>
        <Card>
            <CardHeader>
                <div className="flex justify-between items-start">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            {t('adminSettings.import.title')}
                            {isMissing && (
                                <span
                                    title={t("adminSettings.missingConfiguration.tooltip")}
                                    aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                                >
                                    <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                </span>
                            )}
                        </CardTitle>
                        <Text className="mt-1 text-sm text-muted-foreground">
                            {t('adminSettings.import.description')}
                        </Text>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Upload Section */}
                {!importing && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".mdb"
                                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                                className="hidden"
                                id="mdb-file-input"
                            />
                            <label
                                htmlFor="mdb-file-input"
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
                            >
                                <FileText className="h-5 w-5 text-gray-400" />
                                <Text className="text-sm">
                                    {t('adminSettings.import.upload.dragAndDrop')}
                                </Text>
                            </label>
                        </div>
                        <Text className="text-xs text-muted-foreground">
                            {t('adminSettings.import.upload.fileRequirements')}
                        </Text>
                    </div>
                )}

                {/* Progress Section */}
                {importing && currentStatus && (
                    <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center justify-between">
                            <Text className="font-medium text-blue-900 dark:text-blue-100">
                                {t('adminSettings.import.progress.title')}
                            </Text>
                            {getStatusBadge(currentStatus.status)}
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <Text className="text-sm text-blue-700 dark:text-blue-300">
                                    {t('adminSettings.import.progress.currentPhase')}: {formatPhase(currentStatus.phase)}
                                </Text>
                                <Text className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                    {currentStatus.progress}%
                                </Text>
                            </div>
                            <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2.5">
                                <div
                                    className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full transition-all duration-500"
                                    style={{ width: `${currentStatus.progress}%` }}
                                />
                            </div>
                        </div>

                        {currentStatus.status === 'FAILED' && currentStatus.errors && (
                            <div className="bg-red-50 dark:bg-red-950 border-l-4 border-red-400 dark:border-red-500 p-4 rounded">
                                <Text className="text-red-700 dark:text-red-300 text-sm">
                                    {currentStatus.errors}
                                </Text>
                            </div>
                        )}
                    </div>
                )}

                {/* Import warning without a pending action (e.g. account enabled but nothing found) */}
                {currentStatus?.warnings
                    && currentStatus.socialAccountConfirmationStatus !== 'PENDING'
                    && currentStatus.id !== dismissedWarningSessionId && (
                    <div className="relative bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4 pr-10 rounded">
                        <button
                            type="button"
                            onClick={dismissWarning}
                            aria-label={t('adminSettings.import.socialAccount.dismissWarning')}
                            className="absolute top-2 right-2 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <Text className="font-medium text-amber-800 dark:text-amber-200">
                            {t('adminSettings.import.socialAccount.warningTitle')}
                        </Text>
                        <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            {formatWarnings(currentStatus.warnings)}
                        </Text>
                    </div>
                )}

                {/* Pending Sozialkonto confirmation (survives a reload / disconnect) */}
                {currentStatus?.status === 'COMPLETED'
                    && currentStatus.socialAccountConfirmationStatus === 'PENDING' && (
                    <div className="flex items-center justify-between gap-4 bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4 rounded">
                        <div>
                            <Text className="font-medium text-amber-800 dark:text-amber-200">
                                {t('adminSettings.import.socialAccount.pendingCta.title')}
                            </Text>
                            {currentStatus.warnings && (
                                <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                    {formatWarnings(currentStatus.warnings)}
                                </Text>
                            )}
                            <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                {t('adminSettings.import.socialAccount.pendingCta.description')}
                            </Text>
                        </div>
                        <Button onClick={() => promptSocialConfirmationIfPending(currentStatus)}>
                            {t('adminSettings.import.socialAccount.pendingCta.button')}
                        </Button>
                    </div>
                )}

                {/* History Section */}
                <div>
                    <Text className="font-medium mb-3">{t('adminSettings.import.history.title')}</Text>
                    {history.length === 0 ? (
                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                            <Text className="text-blue-700 dark:text-blue-300">
                                {t('adminSettings.import.history.noItems')}
                            </Text>
                        </div>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('adminSettings.import.history.columns.status')}</TableHeader>
                                    <TableHeader>{t('adminSettings.import.history.columns.members')}</TableHeader>
                                    <TableHeader>{t('adminSettings.import.history.columns.orders')}</TableHeader>
                                    <TableHeader>{t('adminSettings.import.history.columns.dataRange')}</TableHeader>
                                    <TableHeader>{t('adminSettings.import.history.columns.startedAt')}</TableHeader>
                                    <TableHeader>{t('adminSettings.import.history.columns.duration')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((session) => (
                                    <TableRow key={session.id}>
                                        <TableCell>
                                            {getStatusBadge(session.status)}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {session.importedMembersCount ?? '-'}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {session.importedOrdersCount ?? '-'}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateRange(session.dataStartDate, session.dataEndDate)}
                                        </TableCell>
                                        <TableCell>
                                            {formatTimestamp(session.startedAt)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDuration(session.durationSeconds)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </CardContent>
        </Card>

        {/* Opening Balance Dialog */}
        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
            <DialogTitle>{t('adminSettings.import.upload.button')}</DialogTitle>
            <DialogDescription>
                {selectedFile && t('adminSettings.import.upload.selectedFile', { fileName: selectedFile.name })}
            </DialogDescription>
            <DialogBody className="space-y-4">
                <SwitchField>
                    <Label>{t('adminSettings.import.upload.openingBalance.toggle')}</Label>
                    <Switch
                        checked={openingBalanceEnabled}
                        onChange={setOpeningBalanceEnabled}
                        color="blue"
                    />
                </SwitchField>

                {openingBalanceEnabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-300 dark:border-blue-700">
                        <Field>
                            <Label>{t('adminSettings.import.upload.openingBalance.amount')}</Label>
                            <Input
                                type="number"
                                step="0.01"
                                placeholder={t('adminSettings.import.upload.openingBalance.amountPlaceholder')}
                                value={openingBalanceAmount}
                                onChange={(e) => setOpeningBalanceAmount(e.target.value)}
                            />
                        </Field>

                        <Field>
                            <Label>{t('adminSettings.import.upload.openingBalance.date')}</Label>
                            <Input
                                type="date"
                                value={openingBalanceDate}
                                onChange={(e) => setOpeningBalanceDate(e.target.value)}
                            />
                        </Field>
                    </div>
                )}

                <Text className="text-xs text-gray-600 dark:text-gray-400">
                    {t('adminSettings.import.upload.openingBalance.help')}
                </Text>
            </DialogBody>
            <DialogActions>
                <Button plain onClick={handleDialogClose}>
                    {t('adminSettings.import.upload.cancel')}
                </Button>
                <Button onClick={handleUpload} disabled={uploading}>
                    {uploading ? (
                        <>
                            <LoadingSpinner />
                            {t('adminSettings.import.upload.uploadInProgress')}
                        </>
                    ) : (
                        <>
                            <Upload className="h-5 w-5 mr-2" data-slot="icon" />
                            {t('adminSettings.import.upload.button')}
                        </>
                    )}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Sozialkonto Confirmation Dialog */}
        <Dialog open={isSocialConfirmOpen} onClose={() => setIsSocialConfirmOpen(false)}>
            <DialogTitle>{t('adminSettings.import.socialAccount.dialog.title')}</DialogTitle>
            <DialogDescription>
                {t('adminSettings.import.socialAccount.dialog.description')}
            </DialogDescription>
            <DialogBody className="space-y-4">
                {currentStatus?.warnings && (
                    <div className="bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4 rounded">
                        <Text className="font-medium text-amber-800 dark:text-amber-200">
                            {t('adminSettings.import.socialAccount.warningTitle')}
                        </Text>
                        <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            {formatWarnings(currentStatus.warnings)}
                        </Text>
                    </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                    <Text className="text-sm text-blue-800 dark:text-blue-200">
                        {t('adminSettings.import.socialAccount.dialog.breakdown', {
                            credits: currentStatus?.socialAccountDetectedCredits ?? 0,
                            debits: currentStatus?.socialAccountDetectedDebits ?? 0,
                            net: currentStatus?.socialAccountDetectedNet ?? 0
                        })}
                    </Text>
                </div>

                <Field>
                    <Label>{t('adminSettings.import.socialAccount.dialog.amountLabel')}</Label>
                    <Input
                        type="number"
                        step="1"
                        value={socialConfirmAmount}
                        onChange={(e) => setSocialConfirmAmount(e.target.value)}
                    />
                </Field>

                <Text className="text-xs text-gray-600 dark:text-gray-400">
                    {t('adminSettings.import.socialAccount.dialog.amountHelp')}
                </Text>
            </DialogBody>
            <DialogActions>
                <Button plain onClick={() => setIsSocialConfirmOpen(false)}>
                    {t('adminSettings.import.socialAccount.dialog.cancel')}
                </Button>
                <Button onClick={handleConfirmSocialBalance} disabled={confirmingSocial}>
                    {confirmingSocial ? (
                        <>
                            <LoadingSpinner />
                            {t('adminSettings.import.socialAccount.dialog.confirm')}
                        </>
                    ) : (
                        t('adminSettings.import.socialAccount.dialog.confirm')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    )
}
